import type { DevDocsBylineConnection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { dedupeAppIds, getDevDocsAppId } from '../utils/bylineAppIdentity.ts';
import { MAX_BYLINE_CONNECTION_ICONS } from '../utils/getBylineConnectionIcons.ts';

import type { IncomingLinksSsrState } from './incomingLinksSsrState.ts';
import type { OutboundBylineSsrState } from './outboundBylineSsrState.ts';
import type { RelatedContentSsrState } from './relatedContentSsrState.ts';
import { shouldPaintBylineSsrPlaceholder } from './shouldPaintBylineSsrPlaceholder.ts';

export type BylinePlaceholderIconCountInput = {
	outboundState: OutboundBylineSsrState | undefined;
	incomingLinksState: IncomingLinksSsrState | undefined;
	relatedContentState: RelatedContentSsrState | undefined;
	devDocsConnection: DevDocsBylineConnection | null;
};

const sampledAppIds = (
	state: IncomingLinksSsrState | RelatedContentSsrState | OutboundBylineSsrState | undefined,
): readonly string[] => (state?.status === 'present' ? state.appIds : []);

/**
 * How many icon tiles the server paints for the byline: the distinct apps the preloaded samples
 * identified across Dev Docs, Related content, Appears in and Links to, capped at the stack size.
 *
 * Zero when the page does not reach the byline's row minimum (see
 * `shouldPaintBylineSsrPlaceholder`). Otherwise at least one: a page that shows the byline has at
 * least one connection, even when none of its links identified an app (third-party hyperlinks
 * only). The client renders exactly this many icons, so the row never grows or shrinks after
 * paint. CCINT-9543
 */
export const getBylinePlaceholderIconCount = ({
	outboundState,
	incomingLinksState,
	relatedContentState,
	devDocsConnection,
}: BylinePlaceholderIconCountInput): number => {
	const paints = shouldPaintBylineSsrPlaceholder({
		outboundState,
		incomingLinksState,
		relatedContentState,
		hasDevDocsConnection: devDocsConnection !== null,
	});
	if (!paints) {
		return 0;
	}

	// Same order the stack fills in, so the first three ids are the apps the client will draw.
	const appIds = dedupeAppIds([
		getDevDocsAppId(devDocsConnection?.provider),
		...sampledAppIds(relatedContentState),
		...sampledAppIds(incomingLinksState),
		...sampledAppIds(outboundState),
	]);

	// Floor at one, not zero: the byline is going to show (the paint rule above said so), and the
	// client always draws at least one icon in it. Zero here would skip the placeholder and the
	// byline would pop in after hydration. This is the "rows reached the minimum but none named an
	// app" case, e.g. three links to websites that are not on the allowlist.
	return Math.min(MAX_BYLINE_CONNECTION_ICONS, Math.max(1, appIds.length));
};

/**
 * How many icons the loaded stack shows: exactly the tiles the server painted, or `undefined` when
 * it painted none, which includes every client-side navigation. The count comes from
 * `useBylineTileReservation`, captured once per page. CCINT-9543
 */
export const getPaintedIconCount = (reservedIconCount: number): number | undefined =>
	reservedIconCount > 0 ? reservedIconCount : undefined;
